import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Form, Pagination } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../layout/header/Header';
import Footer from '../../layout/footer/Footer';
import './Collections.css';
import {
    fetchStoreCategories,
    fetchStoreProducts,
    fetchStoreProductsPage,
    getEffectivePrice,
    getFallbackProductImage,
    resolveProductImage,
    type StoreCategory,
    type StoreProduct,
} from '../../data/productsApi';

const formatCurrency = (value: number) => `Rs ${Math.round(value).toLocaleString()}`;

const Collections = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<StoreProduct[]>([]);
    const [categories, setCategories] = useState<StoreCategory[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [subcategoryFilter, setSubcategoryFilter] = useState('all');
    const [sizeFilter, setSizeFilter] = useState('all');
    const [sortPreset, setSortPreset] = useState<'recommended' | 'latest' | 'price_low' | 'price_high'>('recommended');
    const [page, setPage] = useState(1);
    const pageSize = 12;

    const [priceMin, setPriceMin] = useState(0);
    const [priceMax, setPriceMax] = useState(0);
    const [selectedMin, setSelectedMin] = useState(0);
    const [selectedMax, setSelectedMax] = useState(0);
    const [availableSubcategories, setAvailableSubcategories] = useState<string[]>([]);
    const [availableSizes, setAvailableSizes] = useState<string[]>([]);
    const lastRequestKeyRef = useRef('');

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    useEffect(() => {
        fetchStoreCategories()
            .then((items) => setCategories(items))
            .catch(() => setCategories([]));
    }, []);

    useEffect(() => {
        let mounted = true;

        const loadPriceBounds = async () => {
            try {
                const result = await fetchStoreProducts(100);
                if (!mounted || !result.length) {
                    if (mounted) {
                        setPriceMin(0);
                        setPriceMax(0);
                        setSelectedMin(0);
                        setSelectedMax(0);
                    }
                    return;
                }

                const prices = result.map((product) => Number(product.price ?? 0));
                const minValue = Math.floor(Math.min(...prices));
                const maxValue = Math.ceil(Math.max(...prices));
                const subcategories = Array.from(
                    new Set(
                        result
                            .map((product) => product.subcategory?.trim())
                            .filter((value): value is string => Boolean(value)),
                    ),
                ).sort((left, right) => left.localeCompare(right));
                const sizes = Array.from(
                    new Set(
                        result
                            .flatMap((product) => (Array.isArray(product.sizes) ? product.sizes : []))
                            .map((value) => value.trim())
                            .filter(Boolean),
                    ),
                ).sort((left, right) => left.localeCompare(right));
                setPriceMin(minValue);
                setPriceMax(maxValue);
                setSelectedMin(minValue);
                setSelectedMax(maxValue);
                setAvailableSubcategories(subcategories);
                setAvailableSizes(sizes);
            } catch {
                if (!mounted) return;
                setPriceMin(0);
                setPriceMax(0);
                setSelectedMin(0);
                setSelectedMax(0);
                setAvailableSubcategories([]);
                setAvailableSizes([]);
            }
        };

        loadPriceBounds();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!categories.length) return;

        const rawCategory = (searchParams.get('category') || 'all').trim();
        if (!rawCategory || rawCategory.toLowerCase() === 'all') {
            if (categoryFilter !== 'all') setCategoryFilter('all');
            return;
        }

        const normalized = rawCategory.toLowerCase();
        const matched = categories.find((category) => category.name.toLowerCase() === normalized);
        if (matched && categoryFilter !== matched._id) {
            setCategoryFilter(matched._id);
            return;
        }

        if (!matched) {
            setCategoryFilter('all');
            setSearchParams({ category: 'all' }, { replace: true });
        }
    }, [searchParams, categories, categoryFilter, setSearchParams]);

    useEffect(() => {
        let mounted = true;
        const loadProducts = async () => {
            const sortConfig =
                sortPreset === 'price_low'
                    ? { sortBy: 'price' as const, sortOrder: 'asc' as const }
                    : sortPreset === 'price_high'
                        ? { sortBy: 'price' as const, sortOrder: 'desc' as const }
                        : sortPreset === 'latest'
                            ? { sortBy: 'createdAt' as const, sortOrder: 'desc' as const }
                            : { sortBy: 'createdAt' as const, sortOrder: 'desc' as const };

            const requestKey = JSON.stringify({
                page,
                limit: pageSize,
                category: categoryFilter === 'all' ? null : categoryFilter,
                subcategory: subcategoryFilter === 'all' ? null : subcategoryFilter,
                sizes: sizeFilter === 'all' ? null : sizeFilter,
                minPrice: priceMax > priceMin ? selectedMin : null,
                maxPrice: priceMax > priceMin ? selectedMax : null,
                sortBy: sortConfig.sortBy,
                sortOrder: sortConfig.sortOrder,
            });

            if (requestKey === lastRequestKeyRef.current) {
                return;
            }
            lastRequestKeyRef.current = requestKey;

            setLoading(true);
            setError('');

            try {
                const response = await fetchStoreProductsPage({
                    page,
                    limit: pageSize,
                    category: categoryFilter === 'all' ? undefined : categoryFilter,
                    subcategory: subcategoryFilter === 'all' ? undefined : subcategoryFilter,
                    sizes: sizeFilter === 'all' ? undefined : sizeFilter,
                    minPrice: priceMax > priceMin ? selectedMin : undefined,
                    maxPrice: priceMax > priceMin ? selectedMax : undefined,
                    ...sortConfig,
                });

                if (!mounted) return;
                setProducts(response.products);
                setTotal(response.total);
                setTotalPages(response.totalPages);
            } catch (err: any) {
                if (!mounted) return;
                setError(err?.response?.data?.message || 'Unable to load products from server.');
                setProducts([]);
                setTotal(0);
                setTotalPages(0);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        loadProducts();

        return () => {
            mounted = false;
        };
    }, [page, pageSize, categoryFilter, subcategoryFilter, sizeFilter, sortPreset, selectedMin, selectedMax, priceMin, priceMax]);

    useEffect(() => {
        setPage(1);
    }, [categoryFilter, subcategoryFilter, sizeFilter, sortPreset, selectedMin, selectedMax]);

    const categoriesLabelMap = useMemo(() => {
        return Object.fromEntries(categories.map((category) => [category._id, category.name]));
    }, [categories]);

    return (
        <div className="arkab-collections-page">
            <Header />

            <section className="arkab-collections-content py-5">
                <div className="container arkab-shop-shell">
                    {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

                    <div className="arkab-breadcrumb"><button type="button" onClick={() => navigate('/')}>Home</button> &gt; <span>All Products</span></div>
                    <h1 className="arkab-shop-title">All Products</h1>
                    <p className="arkab-shop-subtitle">Discover premium watches and jewellery, curated for timeless everyday style.</p>

                    <div className="arkab-shop-layout">
                        <aside className="arkab-shop-sidebar">
                            <div className="arkab-sidebar-block">
                                <h3>Browse by</h3>
                                <ul>
                                    <li>
                                        <button
                                            type="button"
                                            className={categoryFilter === 'all' ? 'active' : ''}
                                            onClick={() => {
                                                setSearchParams({ category: 'all' });
                                            }}
                                        >
                                            All Products
                                        </button>
                                    </li>
                                    {categories.map((category) => (
                                        <li key={category._id}>
                                            <button
                                                type="button"
                                                className={categoryFilter === category._id ? 'active' : ''}
                                                onClick={() => {
                                                    setSearchParams({ category: category.name });
                                                }}
                                            >
                                                {category.name}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="arkab-sidebar-block">
                                <h3>Filter by</h3>
                                <div className="arkab-price-label">Subcategory</div>
                                <Form.Select
                                    className="mb-3"
                                    value={subcategoryFilter}
                                    onChange={(event) => setSubcategoryFilter(event.target.value)}
                                    aria-label="Filter by subcategory"
                                >
                                    <option value="all">All Subcategories</option>
                                    {availableSubcategories.map((subcategory) => (
                                        <option key={subcategory} value={subcategory}>{subcategory}</option>
                                    ))}
                                </Form.Select>

                                <div className="arkab-price-label">Size</div>
                                <Form.Select
                                    className="mb-3"
                                    value={sizeFilter}
                                    onChange={(event) => setSizeFilter(event.target.value)}
                                    aria-label="Filter by size"
                                >
                                    <option value="all">All Sizes</option>
                                    {availableSizes.map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </Form.Select>

                                <div className="arkab-price-label">Price</div>
                                <div className="arkab-price-ranges">
                                    <input
                                        type="range"
                                        min={priceMin}
                                        max={priceMax}
                                        value={selectedMin}
                                        onChange={(event) => {
                                            const next = Number(event.target.value);
                                            setSelectedMin(Math.min(next, selectedMax));
                                        }}
                                        disabled={priceMax <= priceMin}
                                    />
                                    <input
                                        type="range"
                                        min={priceMin}
                                        max={priceMax}
                                        value={selectedMax}
                                        onChange={(event) => {
                                            const next = Number(event.target.value);
                                            setSelectedMax(Math.max(next, selectedMin));
                                        }}
                                        disabled={priceMax <= priceMin}
                                    />
                                </div>
                                <div className="arkab-price-values">
                                    <span>PKR {selectedMin.toLocaleString()}</span>
                                    <span>PKR {selectedMax.toLocaleString()}</span>
                                </div>
                            </div>
                        </aside>

                        <div className="arkab-shop-main">
                            <div className="arkab-shop-toolbar">
                                <span>{loading ? 'Loading products...' : `${total} products`}</span>
                                <label>
                                    Sort by:
                                    <Form.Select
                                        value={sortPreset}
                                        onChange={(event) => setSortPreset(event.target.value as 'recommended' | 'latest' | 'price_low' | 'price_high')}
                                    >
                                        <option value="recommended">Recommended</option>
                                        <option value="latest">Newest</option>
                                        <option value="price_low">Price: Low to High</option>
                                        <option value="price_high">Price: High to Low</option>
                                    </Form.Select>
                                </label>
                            </div>

                            <div className="arkab-products-rail">
                                {!products.length ? (
                                    <div className="arkab-empty-state-wrap">
                                        <Alert variant="light" className="arkab-empty-state">
                                            {loading ? 'Loading products...' : 'No products found. Try another category or price range.'}
                                        </Alert>
                                    </div>
                                ) : null}

                                {products.map((product) => {
                                    const image = Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : '';
                                    const imageUrl = resolveProductImage(image || undefined);
                                    const discounted = product.discount && product.discount > 0;
                                    const discountedPrice = getEffectivePrice(product);
                                    const categoryName = product.categoryId ? (categoriesLabelMap[product.categoryId] || 'Featured') : 'Featured';

                                    return (
                                        <article className="arkab-rail-card" key={product._id}>
                                            <div className="arkab-rail-image-wrap" onClick={() => navigate(`/products/${product._id}`)}>
                                                <img src={imageUrl} alt={product.name} onError={(event) => { event.currentTarget.src = getFallbackProductImage(); }} />
                                                {discounted ? <span className="arkab-arrival-badge">New Arrival</span> : null}
                                            </div>
                                            <div className="arkab-rail-meta-row">
                                                <span className="arkab-category-chip">{categoryName}</span>
                                                {discounted ? <span className="arkab-discount-chip">-{product.discount}%</span> : null}
                                            </div>
                                            <h3 onClick={() => navigate(`/products/${product._id}`)}>{product.name}</h3>
                                            <div className="arkab-price-row">
                                                {discounted ? <span className="arkab-price-old">{formatCurrency(product.price)}</span> : null}
                                                <p>{formatCurrency(discountedPrice)}</p>
                                            </div>
                                            <button className="arkab-view-btn" onClick={() => navigate(`/products/${product._id}`)}>View Details</button>
                                        </article>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
                            <small>Page {page} of {totalPages}</small>
                            <Pagination className="mb-0 theme-pagination">
                                <Pagination.Prev
                                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                    disabled={page <= 1}
                                />
                                {Array.from({ length: totalPages }).map((_, index) => {
                                    const pageNo = index + 1;
                                    return (
                                        <Pagination.Item
                                            key={`page-${pageNo}`}
                                            active={pageNo === page}
                                            onClick={() => setPage(pageNo)}
                                        >
                                            {pageNo}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next
                                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                    disabled={page >= totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Collections;
